(() => {
  if (customElements.get('bps-media-slider')) return;

  const THUMBS_SPACE = 12;
  const THUMBS_SPACE_MOBILE = 12;
  const THUMBS_PER_VIEW = 4;
  const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
  const THUMB_ACTIVE_CLASS = 'swiper-slide-thumb-active';

  class BpsMediaSlider extends HTMLElement {
    constructor() {
      super();
      this.onSectionLoad = this.onSectionLoad.bind(this);
      this.onWindowLoad = this.onWindowLoad.bind(this);
      this.onThumbClick = this.onThumbClick.bind(this);
      this.onPrevNavClick = this.onPrevNavClick.bind(this);
      this.onNextNavClick = this.onNextNavClick.bind(this);
      this.onResize = this.onResize.bind(this);
      this.mainSwiper = null;
      this.thumbsSwiper = null;
      this.mobileMediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    }

    connectedCallback() {
      this.mainEl = this.querySelector('[data-main-slider]');
      this.thumbsEl = this.querySelector('[data-thumbs-slider]');
      this.prevButton = this.querySelector('[data-thumbs-prev]');
      this.nextButton = this.querySelector('[data-thumbs-next]');

      if (!this.mainEl) return;

      this.classList.remove('is-initialized');

      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.addEventListener('shopify:section:load', this.onSectionLoad);
      }

      if (typeof Swiper === 'undefined') {
        window.addEventListener('load', this.onWindowLoad, { once: true });
        return;
      }

      this.initSwipers();
      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      if (typeof Shopify !== 'undefined' && Shopify.designMode) {
        document.removeEventListener('shopify:section:load', this.onSectionLoad);
      }

      window.removeEventListener('load', this.onWindowLoad);
      window.removeEventListener('resize', this.onResize);
      this.destroySwipers();
    }

    onWindowLoad() {
      this.initSwipers();
    }

    onSectionLoad(event) {
      if (!event.target.contains(this)) return;
      this.destroySwipers();
      this.initSwipers();
    }

    getThumbsSpace() {
      return this.mobileMediaQuery.matches ? THUMBS_SPACE_MOBILE : THUMBS_SPACE;
    }

    onResize() {
      if (this.thumbsSwiper) {
        this.thumbsSwiper.params.spaceBetween = this.getThumbsSpace();
        this.thumbsSwiper.update();
        this.normalizeThumbHeights();
        this.syncThumbsPosition(0);
      }

      if (this.mainSwiper) {
        this.mainSwiper.update();
      }
    }

    getThumbSlides() {
      if (!this.thumbsEl) return [];
      return Array.from(this.thumbsEl.querySelectorAll('.swiper-slide'));
    }

    getThumbSlideCount() {
      return this.getThumbSlides().length;
    }

    normalizeThumbHeights() {
      if (!this.thumbsEl) return;

      this.thumbsEl.style.height = 'auto';

      const wrapper = this.thumbsEl.querySelector('.swiper-wrapper');
      if (wrapper) {
        wrapper.style.height = 'auto';
      }

      this.getThumbSlides().forEach((slide) => {
        slide.style.height = 'auto';
      });
    }

    getThumbsMaxIndex() {
      return Math.max(0, this.getThumbSlideCount() - THUMBS_PER_VIEW);
    }

    getThumbsScrollIndex(imageIndex) {
      return Math.min(imageIndex, this.getThumbsMaxIndex());
    }

    getMainMaxIndex() {
      if (!this.mainSwiper) return 0;
      return Math.max(0, this.mainSwiper.slides.length - 1);
    }

    setActiveThumb(index) {
      if (!this.thumbsEl) return;

      this.thumbsEl.querySelectorAll('.swiper-slide').forEach((slide, slideIndex) => {
        slide.classList.toggle(THUMB_ACTIVE_CLASS, slideIndex === index);
      });
    }

    goToSlide(index, speed = 0) {
      if (!this.mainSwiper) return;

      const targetIndex = Math.min(Math.max(index, 0), this.getMainMaxIndex());
      this.mainSwiper.slideTo(targetIndex, speed);
      this.syncThumbsPosition(speed);
    }

    onThumbClick(event) {
      const slide = event.target.closest('.swiper-slide');
      if (!slide || !this.mainSwiper || !this.thumbsSwiper) return;

      const index = Array.prototype.indexOf.call(slide.parentElement.children, slide);
      if (index < 0) return;

      event.preventDefault();
      this.goToSlide(index, 0);
    }

    onPrevNavClick(event) {
      event.preventDefault();
      if (!this.mainSwiper || this.mainSwiper.activeIndex <= 0) return;
      this.goToSlide(this.mainSwiper.activeIndex - 1);
    }

    onNextNavClick(event) {
      event.preventDefault();
      if (!this.mainSwiper || this.mainSwiper.activeIndex >= this.getMainMaxIndex()) return;
      this.goToSlide(this.mainSwiper.activeIndex + 1);
    }

    updateNavState() {
      if (!this.mainSwiper || !this.prevButton || !this.nextButton) return;

      const isBeginning = this.mainSwiper.activeIndex <= 0;
      const isEnd = this.mainSwiper.activeIndex >= this.getMainMaxIndex();

      this.prevButton.classList.toggle('swiper-button-disabled', isBeginning);
      this.nextButton.classList.toggle('swiper-button-disabled', isEnd);
      this.prevButton.disabled = isBeginning;
      this.nextButton.disabled = isEnd;
    }

    syncThumbsPosition(speed = 0) {
      if (!this.mainSwiper) return;

      const activeIndex = this.mainSwiper.activeIndex;
      this.setActiveThumb(activeIndex);

      if (this.thumbsSwiper && this.getThumbSlideCount() > THUMBS_PER_VIEW) {
        this.thumbsSwiper.slideTo(this.getThumbsScrollIndex(activeIndex), speed);
      }

      this.updateNavState();
    }

    bindNavEvents() {
      if (!this.prevButton || !this.nextButton) return;
      this.prevButton.addEventListener('click', this.onPrevNavClick);
      this.nextButton.addEventListener('click', this.onNextNavClick);
    }

    unbindNavEvents() {
      if (!this.prevButton || !this.nextButton) return;
      this.prevButton.removeEventListener('click', this.onPrevNavClick);
      this.nextButton.removeEventListener('click', this.onNextNavClick);
    }

    initSwipers() {
      if (typeof Swiper === 'undefined' || this.mainSwiper || !this.mainEl) return;

      const slideCount = this.mainEl.querySelectorAll('.swiper-slide').length;
      if (!slideCount) return;

      const hasMultipleSlides = slideCount > 1;
      const hasThumbsNav = slideCount > THUMBS_PER_VIEW;
      this.classList.toggle('has-single-slide', !hasMultipleSlides);

      if (hasMultipleSlides && this.thumbsEl) {
        this.thumbsSwiper = new Swiper(this.thumbsEl, {
          spaceBetween: this.getThumbsSpace(),
          slidesPerView: THUMBS_PER_VIEW,
          slidesPerGroup: 1,
          watchSlidesProgress: true,
          slideToClickedSlide: false,
          allowTouchMove: hasThumbsNav,
          speed: 200,
        });

        this.thumbsEl.addEventListener('click', this.onThumbClick);

        if (hasThumbsNav) {
          this.bindNavEvents();
        }

        this.normalizeThumbHeights();
      }

      this.mainSwiper = new Swiper(this.mainEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        speed: 300,
        allowTouchMove: hasMultipleSlides,
        on: {
          init: () => {
            requestAnimationFrame(() => {
              this.classList.add('is-initialized');
              this.thumbsSwiper?.update();
              this.normalizeThumbHeights();
              this.syncThumbsPosition(0);
            });
          },
          slideChange: () => {
            this.syncThumbsPosition(0);
          },
        },
      });
    }

    destroySwipers() {
      this.unbindNavEvents();

      if (this.thumbsEl) {
        this.thumbsEl.removeEventListener('click', this.onThumbClick);
      }

      if (this.mainSwiper) {
        this.mainSwiper.destroy(true, true);
        this.mainSwiper = null;
      }

      if (this.thumbsSwiper) {
        this.thumbsSwiper.destroy(true, true);
        this.thumbsSwiper = null;
      }

      this.classList.remove('is-initialized', 'has-single-slide');
    }
  }

  customElements.define('bps-media-slider', BpsMediaSlider);
})();
